import { useMemo, useState } from "react";
import { ALL_PAGES, HOME } from "./nav";
import { parseCommand, periodFromArg } from "./codes";
import { fuzzyFilter } from "../lib/fuzzy";

export interface Command { id: string; title: string; hint?: string; code?: string; words?: string; run: (arg?: string) => void }

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

// The command bar (Ctrl+K, or start typing anywhere outside a field): an Altekore code, optionally with a period for a
// report (FS03 q2), or any part of a page's name.
export function Palette({ extra, go, initial = "", onClose }: { extra: Command[]; go: (path: string) => void; initial?: string; onClose: () => void }) {
  const [q, setQ] = useState(initial);
  const [sel, setSel] = useState(0);
  const all: Command[] = useMemo(() => [
    { id: "home", title: HOME.title, code: HOME.code, hint: "Home", run: () => go("") },
    ...ALL_PAGES.filter((p) => !p.hidden).map((p) => ({
      id: p.id, title: p.title, hint: p.section.title, code: p.code, words: p.words,
      run: (arg?: string) => {
        const per = arg && p.path.includes("/r/") ? periodFromArg(arg, today()) : null;
        go(per ? `${p.path}?from=${per.from}&to=${per.to}&as_on=${per.to}` : p.path);
      },
    })),
    ...extra,
  ], [extra, go]);

  const { code, arg } = parseCommand(q);
  const exact = code ? (code === HOME.code ? all[0] : all.find((c) => c.code === code)) : undefined;
  const period = exact && arg ? periodFromArg(arg, today()) : null;
  const list = exact ? [exact, ...all.filter((c) => c !== exact && c.code?.startsWith(code!.slice(0, 2)))]
    : fuzzyFilter(all, q, (c) => `${c.code ?? ""} ${c.title} ${c.hint ?? ""} ${c.words ?? ""}`);
  const run = (c?: Command) => { if (c) { onClose(); c.run(c === exact ? arg : undefined); } };

  return (
    <div className="overlay" onMouseDown={onClose}>
      <div className="card palette" role="dialog" aria-label="Command bar" onMouseDown={(e) => e.stopPropagation()}>
        <label className="palette-input">
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
          <input autoFocus placeholder="Type a code (SI01) or search" value={q} aria-label="Code or search"
            onChange={(e) => { setQ(e.target.value); setSel(0); }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, list.length - 1)); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
              else if (e.key === "Enter") { e.preventDefault(); run(list[sel]); }
              else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); }
            }} />
          <kbd>Esc</kbd>
        </label>
        <ul className="list" role="listbox">
          {list.slice(0, 40).map((c, i) => (
            <li key={c.id} role="option" aria-selected={i === sel} className={i === sel ? "sel" : ""} onMouseEnter={() => setSel(i)} onClick={() => run(c)}>
              <span className="code">{c.code ?? ""}</span>
              <span className="what"><span>{c.title}{c.hint && <span className="muted small"> · {c.hint}</span>}</span>
                {c === exact && period && <span className="small period">{period.label}</span>}
                {c === exact && arg && !period && list[0] === c && c.id !== "home" && <span className="small muted period">“{arg}” isn't a period this page takes; it opens as usual</span>}</span>
            </li>
          ))}
          {list.length === 0 && <li className="muted">Nothing matches. Codes are two letters and two digits, like SI01.</li>}
        </ul>
        <p className="palette-foot muted small">↑ ↓ move · Enter open · after a report's code add a period: <b>q2</b>, <b>fy</b>, <b>lm</b>, <b>oct</b></p>
      </div>
    </div>
  );
}

export function Help({ onClose }: { onClose: () => void }) {
  const rows: [string, string][] = [
    ["Ctrl+K, or type outside a field", "Command bar: a code (SI01) or a name"],
    ["SI01 · PB01 · RC01 · PY01 · JE01", "New invoice, bill, receipt, payment, journal"],
    ["FS01 · FS02 · FS03", "Trial balance, profit and loss, balance sheet (add q2, fy, lm, oct …)"],
    ["Ctrl+S", "Save (a draft, on entry screens)"], ["Ctrl+Enter", "Save and post"],
    ["Alt+N", "New, on a list"], ["Alt+D", "Delete or discard"], ["Esc", "Back"], ["?", "This list"],
  ];
  return (
    <div className="overlay" onMouseDown={onClose}>
      <div className="card help" role="dialog" aria-label="Keyboard" onMouseDown={(e) => e.stopPropagation()}>
        <h2>Keyboard and codes</h2>
        <table><tbody>{rows.map(([k, w]) => <tr key={k}><td><kbd>{k}</kbd></td><td>{w}</td></tr>)}</tbody></table>
        <p className="muted small">Every page's code is on the Today page and beside it in the menu.</p>
        <button autoFocus onClick={onClose}>Close <kbd>Esc</kbd></button>
      </div>
    </div>
  );
}
